import {
  projectIssueViewerTimelineSnapshot,
  reconcileIssueViewerPrimary,
} from '@github-ui/preheat-issues-manager/issue-viewer-primary-reconciliation'
import {
  isCanonicalIssueViewerQueryKey,
  type IssueCoordinates,
  type IssueViewerQueryIdentifier,
} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {type QueryClient, queryOptions} from '@github-ui/react-query'
import {IssueTimelineSnapshot} from '@github-ui/server-persisted-queries'
import type {IssueTimelineSnapshotQuery} from '@github-ui/server-persisted-queries/types/IssueTimelineSnapshot'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {TimelineIssue} from './fetch-timeline-items'

export const CANONICAL_TIMELINE_FRONT_COUNT = 15
export const CANONICAL_TIMELINE_BACK_COUNT = CANONICAL_TIMELINE_FRONT_COUNT + 1

export type CanonicalTimelineSnapshot = NonNullable<NonNullable<IssueTimelineSnapshotQuery['repository']>['issue']>

export type CanonicalTimelineSnapshotCoordinates = {
  owner: string
  repo: string
  number: number
}

export function canonicalTimelineSnapshotQueryKey({owner, repo, number}: CanonicalTimelineSnapshotCoordinates) {
  return ['issue-viewer', 'canonical-timeline-snapshot', {owner, repo, number}] as const
}

export async function fetchCanonicalTimelineSnapshot(
  {owner, repo, number}: CanonicalTimelineSnapshotCoordinates,
  signal?: AbortSignal,
): Promise<CanonicalTimelineSnapshot> {
  const search = new URLSearchParams({snapshot: '1', queryId: IssueTimelineSnapshot})
  const response = await verifiedFetchJSON(`/${owner}/${repo}/issues/${number}/timeline_items?${search}`, {
    signal,
    credentials: 'include',
  })
  if (!response.ok) throw new Error(`Canonical timeline snapshot failed (${response.status})`)

  const json = (await response.json()) as {data?: IssueTimelineSnapshotQuery; errors?: unknown[]}
  const issue = json.data?.repository?.issue
  if (!issue?.timelineItems || !issue.backTimelineItems) {
    throw new Error('Canonical timeline snapshot returned no issue')
  }

  return issue
}

export function canonicalTimelineSnapshotQueryOptions(coordinates: CanonicalTimelineSnapshotCoordinates) {
  return queryOptions({
    queryKey: canonicalTimelineSnapshotQueryKey(coordinates),
    queryFn: ({signal}) => fetchCanonicalTimelineSnapshot(coordinates, signal),
    // The snapshot reconciles the persisted primary query; retaining a second persisted copy would
    // create two authorities for the same canonical windows.
    meta: {skipPersister: true},
    staleTime: 0,
  })
}

export async function reconcileCanonicalTimelineSnapshot({
  coordinates,
  primaryIdentifier,
  issueId,
  primaryQueryKey,
  newestSectionQueryKey,
  queryClient,
}: {
  coordinates: IssueCoordinates
  primaryIdentifier: IssueViewerQueryIdentifier
  issueId: string
  primaryQueryKey: readonly unknown[] | undefined
  newestSectionQueryKey?: readonly unknown[]
  queryClient: QueryClient
}): Promise<boolean> {
  if (!primaryQueryKey || !isCanonicalIssueViewerQueryKey(primaryQueryKey)) return false
  if (queryClient.getQueryData(primaryQueryKey) === undefined) return false

  const snapshot = await queryClient.fetchQuery(canonicalTimelineSnapshotQueryOptions(coordinates))
  if (snapshot.id !== issueId) return false
  if (newestSectionQueryKey) {
    queryClient.setQueryData<TimelineIssue>(newestSectionQueryKey, current =>
      current ? {...current, id: snapshot.id, timelineItems: snapshot.backTimelineItems} : current,
    )
  }
  return reconcileIssueViewerPrimary({
    identifier: primaryIdentifier,
    issueId,
    queryClient,
    project: data => projectIssueViewerTimelineSnapshot(data, snapshot),
  })
}
